/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IPriceComponentResources } from '@msdyn365-commerce/components';
import MsDyn365 from '@msdyn365-commerce/core';
import { ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    ICategoryHierarchyViewProps,
    IRefineMenuViewProps,
    ISearchResultContainerViewProps,
    ISearchResultModalViewProps,
    ISortByViewProps,
    ITitleViewProps
} from '@msdyn365-commerce-modules/search-result-container';
import { Button, getTelemetryObject, Module, Node } from '@msdyn365-commerce-modules/utilities';
import React, { MouseEventHandler, useEffect, useState } from 'react';

import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import {
    ICelliSearchResultContainerProps,
    ICelliSearchResultContainerResources
} from './celli-search-result-container.props.autogenerated';
import { ProductComponent } from '../../themes/celli/views/components/product.component';
import { toJS } from 'mobx';
declare global {
    interface Window {
        dataLayer: any[];
    }
}

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @param product - The product item data.
 * @param index - The index of each product item.
 * @param isEnableProductFullTile - The flag for showing Product desription tile.
 * @returns The JSX Element.
 */
const renderProductCardsDescription = (
    props: ISearchResultContainerViewProps & ICelliSearchResultContainerProps<{}>,
    product: ProductSearchResult,
    index: number,
    isEnableProductFullTile?: boolean
): JSX.Element | null => {
    const { config, context, resources, productsDimensionAvailabilities } = props;
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const breadCrumbType = props.context.app.config.breadcrumbType;
    const isAllowBack = props.config.allowBackNavigation && (breadCrumbType === 'back' || breadCrumbType === 'categoryAndBack');
    const classname = isEnableProductFullTile
        ? 'ms-product-search-result__item product__description v-100'
        : 'ms-product-search-result__item product__description';
    const quickviewslot = ArrayExtensions.hasElements(props.slots.quickview) ? props.slots.quickview[0] : undefined;
    const channelInventoryConfigurationId = props.data.products.result?.channelInventoryConfigurationId;
    const isPriceMinMaxFeatureState = props.data.featureState.result?.find(
        featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
    );
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };

    const productComparisonButton = ArrayExtensions.hasElements(props.slots.productComparisonButton)
        ? props.slots.productComparisonButton[0]
        : null;

    const handleSelectItem = () => {
        if (typeof window !== 'undefined') {
            if (!window.dataLayer) {
                window.dataLayer = [];
            }
            (window as any).dataLayer.push({ ecommerce: null });
            const categoryHierarchy = toJS(props.data.categoryHierarchy.result);
            const item = {
                // @ts-ignore
                item_list_id: context.request.listingPageId || '',
                // @ts-ignore
                item_list_name: context.request.listingPageName || '',
                item_id: product.RecordId.toString(),
                item_name: product.Name,
                index,
                // @ts-ignore
                price: product.Price || 0,
                // @ts-ignore
                discount: product.BasePrice && product.Price ? parseFloat((product.Price - product.BasePrice).toFixed(2)) : 0,
                // @ts-ignore
                item_category: categoryHierarchy[0]?.Name || '',
                // @ts-ignore
                item_category2: categoryHierarchy[1]?.Name || '',
                // @ts-ignore
                item_category3: categoryHierarchy[2]?.Name || '',
                // @ts-ignore
                item_category4: categoryHierarchy[3]?.Name || '',
                // @ts-ignore
                item_list_id: props?.data?.category?.result?.RecordId || '',
                // @ts-ignore
                item_list_name: props?.data?.category?.result?.NeutralizedName || ''
            };

            (window as any).dataLayer.push({
                event: 'select_item',
                ecommerce: {
                    item_list_id: item.item_list_id,
                    item_list_name: item.item_list_name,
                    items: [item]
                }
            });
        }
    };

    return (
        <li className={classname} key={index} onClick={handleSelectItem}>
            <ProductComponent
                context={context}
                telemetryContent={telemetryContent}
                imageSettings={config.imageSettings}
                freePriceText={resources.toBeCalculatedLabel}
                originalPriceText={resources.netLabel}
                currentPriceText={resources.currentPriceText}
                ratingAriaLabel={resources.ratingAriaLabel}
                allowBack={isAllowBack}
                id={props.id}
                key={product.RecordId}
                typeName={props.typeName}
                data={{ product }}
                quickViewButton={quickviewslot}
                productComparisonButton={productComparisonButton}
                isEnabledProductDescription={true}
                inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                priceResources={priceResources}
                swatchItemAriaLabel={resources.swatchItemAriaLabel}
                dimensionAvailabilities={productsDimensionAvailabilities?.find(
                    (dimensionAvailability: IProductsDimensionsAvailabilities[]) =>
                        ArrayExtensions.hasElements(dimensionAvailability) &&
                        dimensionAvailability[0].masterProductId === product.MasterProductId
                )}
                skuLabel={resources.skuLabel}
                // @ts-ignore
                discountPercentLabel={resources.discountPercentLabel}
            />
        </li>
    );
};

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @returns The JSX Element.
 */
const renderProducts = (props: ISearchResultContainerViewProps & ICelliSearchResultContainerProps<{}>): JSX.Element | null => {
    const products = props.data.listPageState.result?.activeProducts;

    if (!products || !ArrayExtensions.hasElements(products)) {
        return null;
    }

    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (!window.dataLayer) {
                window.dataLayer = [];
            }

            const categoryHierarchy = toJS(props.data.categoryHierarchy.result);

            window.dataLayer.push({ ecommerce: null });

            const items = products.map((product: ProductSearchResult, index: number) => ({
                // @ts-ignore
                item_id: product.ItemId,
                // @ts-ignore
                item_name: product.Name,
                index: index,
                // @ts-ignore
                item_category: categoryHierarchy[0]?.Name || '',
                // @ts-ignore
                item_category2: categoryHierarchy[1]?.Name || '',
                // @ts-ignore
                item_category3: categoryHierarchy[2]?.Name || '',
                // @ts-ignore
                item_category4: categoryHierarchy[3]?.Name || '',
                // @ts-ignore
                item_list_id: props?.data?.category?.result?.RecordId || '',
                // @ts-ignore
                item_list_name: props?.data?.category?.result?.NeutralizedName || '',
                // @ts-ignore
                price: product.Price
            }));

            window.dataLayer.push({
                event: 'view_item_list',
                ecommerce: {
                    // @ts-ignore
                    item_list_id: props?.data?.category?.result?.RecordId || '',
                    // @ts-ignore
                    item_list_name: props?.data?.category?.result?.Name || '',
                    items: items
                }
            });
        }
    }, [products]);

    return (
        <Node {...props.ProductsContainer}>
            {props.errorMessage}
            <ul className='list-unstyled'>
                {products.map((product: ProductSearchResult, index: number) =>
                    props.config.enableProductFullTile
                        ? renderProductCardsDescription(props, product, index, true)
                        : renderProductCardsDescription(props, product, index, false)
                )}
            </ul>
        </Node>
    );
};

/**
 * Render Title Count.
 * @param props - The title view props.
 * @returns The JSX Element.
 */
const renderTitleCount = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return <Node {...TitleContainer}>{title.titleCount}</Node>;
    }
    return null;
};

/**
 * Render Category Hierarchy.
 * @param props - The category hierarchy view props.
 * @returns The JSX Element.
 */
const renderCategoryHierarchy = (props: ICategoryHierarchyViewProps): JSX.Element | null => {
    const { CategoryHierarchyContainer, categoryHierarchyList, categoryHierarchySeparator } = props;
    if (categoryHierarchyList) {
        return (
            <Node {...CategoryHierarchyContainer}>
                {categoryHierarchyList.map((category, index) => (
                    <React.Fragment key={index}>
                        {category}
                        {categoryHierarchyList[index + 1] && categoryHierarchySeparator}
                    </React.Fragment>
                ))}
            </Node>
        );
    }

    return null;
};

/**
 * Function to make filter sticky.
 * @param isMobile - The boolean prop.
 * @param isStickyHeader - The boolean prop.
 */
export const configureStickyFilter = (isMobile: boolean, isStickyHeader: boolean): void => {
    // Get heights of cookie and promotion banners
    const defaultValue = 0;
    const defaultOffset = 28;
    const headerContainer: HTMLElement | null = document.querySelector('.ms-header');

    const headerHeight: number | undefined = headerContainer?.offsetHeight;
    const containerHeight: number = headerHeight ?? defaultOffset;
    const bannerHeights = isStickyHeader ? defaultValue : containerHeight;
    const headerElement = document.querySelector('header .lock-opaque');
    const defaultContainer = document.querySelector('header .default-container');
    const refinerElement = isMobile ? document.querySelector('.msc-sort-filter-wrapper') : document.querySelector('.ms-refiner-heading');
    if (refinerElement && headerElement && defaultContainer) {
        if (MsDyn365.isBrowser && document.documentElement.scrollTop > bannerHeights) {
            refinerElement.classList.add('lock-Filter');
            refinerElement.setAttribute('style', `top: ${defaultContainer.clientHeight}px`);
        } else {
            refinerElement.classList.remove('lock-Filter');
        }
    } else if (refinerElement) {
        if (MsDyn365.isBrowser && document.documentElement.scrollTop > bannerHeights) {
            refinerElement.classList.add('lock-top');
        } else {
            refinerElement.classList.remove('lock-top');
            refinerElement.classList.remove('lock-Filter');
            refinerElement.removeAttribute('style');
        }
    }
};

/**
 * Function to setup filter sticky.
 * @param isMobile - The boolean prop.
 * @param isStickyHeader - The boolean prop.
 */
function stickyFilterSetup(isMobile: boolean, isStickyHeader: boolean): void {
    window.addEventListener('scroll', () => {
        configureStickyFilter(isMobile, isStickyHeader);
    });
    configureStickyFilter(isMobile, isStickyHeader);
}

/**
 * Function to make filter sticky.
 * @param refinerRefernce - Ref prop for filter.
 * @param toggle - Toggle function for filter menu.
 */
function useOutsideClick(refinerRefernce: React.RefObject<HTMLElement>, toggle: (event: Event) => void) {
    React.useEffect(() => {
        /**
         * Function to make filter sticky.
         * @param event - Event prop for filter.
         */
        function handleClickOutside(event: Event) {
            const element = document.querySelector('.ms-refine-filter__toggle_collapsed');
            if (refinerRefernce.current && !refinerRefernce.current.contains(event.target as Node) && !element) {
                toggle(event);
            }
        }
        if (MsDyn365.isBrowser) {
            document.addEventListener('click', handleClickOutside);
        }
        return () => {
            if (MsDyn365.isBrowser) {
                document.removeEventListener('click', handleClickOutside);
            }
        };
    }, [refinerRefernce, toggle]);
}

/**
 * Render the category page items.
 * @param props - The view props.
 * @returns The search Result container module view.
 */
const SearchResultContainerView: React.FC<ISearchResultContainerViewProps & ICelliSearchResultContainerProps<{}>> = props => {
    const {
        SearchResultContainer,
        pagination,
        choiceSummary,
        isMobile,
        modalToggle,
        searchResultModal,
        TitleViewProps,
        refineMenu,
        categoryHierarchy,
        sortByOptions,
        CategoryNavContainer,
        RefineAndProductSectionContainer,
        FeatureSearchContainer,
        similarLookProduct,
        errorMessage,
        resources,
        context
    } = props;

    // @ts-ignore
    const isUserAuthenticated = context?.request?.User?.IsAuthenticated;

    useEffect(() => {
        if (props.config.useStickyFilter && MsDyn365.isBrowser) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- app context is generic
            stickyFilterSetup(props.isMobile, props.context.app.config.enableStickyHeader as boolean);
        }
    }, [props]);

    useEffect(() => {
        // Function to toggle submenu
        const toggleSubmenu = (event: MouseEvent) => {
            // @ts-ignore
            const button = event.target.closest('.ms-refine-submenu.list-group');
            if (button) {
                const submenu = button.querySelector('.collapse');
                if (submenu) {
                    const isClosed = submenu.style.height === '0px' || getComputedStyle(submenu).height === '0px';
                    submenu.style.height = isClosed ? 'auto' : '0';
                    submenu.style.display = isClosed ? 'block' : 'none';
                }
            }
        };

        // Set initial styles for submenus
        const setupRefiners = () => {
            document.querySelectorAll('.ms-refine-submenu.list-group').forEach(group => {
                const submenu = group.querySelector('.collapse');
                if (submenu) {
                    // @ts-ignore
                    submenu.style.height = '0';
                    // @ts-ignore
                    submenu.style.overflow = 'hidden';
                    // @ts-ignore
                    submenu.style.transition = 'height 0.3s ease';
                }
            });
        };

        // Add event listener for click to handle submenu toggle
        document.addEventListener('click', toggleSubmenu);

        // Setup refiners
        setupRefiners();

        // Cleanup event listener on component unmount
        return () => {
            document.removeEventListener('click', toggleSubmenu);
        };
    }, []);

    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const toggle = () => {
        setIsExpanded(!isExpanded);
    };
    const wrapperRefiner = React.useRef(null);
    useOutsideClick(wrapperRefiner, toggle);
    const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;

    if (isMobile) {
        return (
            <Module {...SearchResultContainer}>
                {similarLookProduct ? null : (
                    <Node {...CategoryNavContainer}>
                        {renderCategoryHierarchy(categoryHierarchy)}
                        {renderTitle(TitleViewProps)}
                        {renderTitleCount(TitleViewProps)}
                    </Node>
                )}
                {choiceSummary}
                <Node className='msc-sort-filter-wrapper'>{modalToggle}</Node>
                {createSearchResultModal(searchResultModal, refineMenu, sortByOptions, isRecoSearchPage)}
                <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                {errorMessage}
                {renderProducts(props)}
                {pagination}
            </Module>
        );
    }

    return (
        <Module {...SearchResultContainer}>
            {similarLookProduct ? null : (
                <Node {...CategoryNavContainer}>
                    {categoryHierarchy && renderCategoryHierarchy(categoryHierarchy)}
                    {TitleViewProps && renderTitle(TitleViewProps)}
                    {TitleViewProps && renderTitleCount(TitleViewProps)}
                </Node>
            )}
            <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
            <Node {...RefineAndProductSectionContainer}>
                <Node ref={wrapperRefiner} className='ms-refiner-heading'>
                    <Node className='ms-refiner-heading_title'>
                        <Button
                            className={isExpanded ? 'ms-refine-filter__toggle_expanded' : 'ms-refine-filter__toggle_collapsed'}
                            aria-label='Filter'
                            onClick={toggle}
                            aria-expanded={isExpanded}
                            tabIndex={0}
                        >
                            {resources.filterText}
                        </Button>
                        <div className='ms-search-result-wrapper-title-choice-summary'>{choiceSummary}</div>
                        <div className='ms-search-result-wrapper-sort-by-category'>
                            {sortByOptions && !isRecoSearchPage && renderSort(sortByOptions, '', isUserAuthenticated)}
                        </div>
                    </Node>
                    {refineMenu && isExpanded && renderRefiner(refineMenu, isUserAuthenticated)}
                    <Node className='ms-refine__footer'>
                        {refineMenu && isExpanded && renderRefinerFooter(refineMenu, toggle, resources)}
                    </Node>
                </Node>

                <Node className='ms-search-result-container__search-result-container'>
                    <div className='ms-search-result-container__refine-menu__filters-container'>
                        {renderRefiner(refineMenu, isUserAuthenticated)}
                    </div>
                    <div className='ms-search-result-container__refine-menu__products-container'>
                        {errorMessage}
                        {renderProducts(props)}
                        {pagination}
                    </div>
                </Node>
            </Node>
        </Module>
    );
};

/**
 * Render Search result Modal.
 * @param modalProps - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createSearchResultModal = (
    modalProps: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element => {
    return React.cloneElement(
        modalProps.modal,
        {},
        modalProps.modalHeader,
        createModalBody(modalProps, refineMenu, sortByDropDown, isRecoSearchPage),
        modalProps.modalFooter
    );
};

/**
 * Render Refiner Mobile.
 * @param props - The Refine Menu view props.
 * @returns The JSX Element.
 */
const renderRefinerMobile = (props: IRefineMenuViewProps): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {refiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render Search result Body.
 * @param props - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createModalBody = (
    props: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element | null => {
    if (sortByDropDown) {
        return React.cloneElement(props.modalBody, {}, renderSort(sortByDropDown, isRecoSearchPage), renderRefinerMobile(refineMenu));
    }
    return null;
};

/**
 * Render Refiner.
 * @param props - The Refine Menu view props.
 * @returns The JSX Element.
 */
const renderRefiner = (props: IRefineMenuViewProps, isUserAuthenticated: boolean): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;

    // @ts-ignore
    const filteredRefiners = refiners.filter(refiner => {
        if (!isUserAuthenticated) {
            // @ts-ignore
            return refiner.key !== '1';
        }
        return true;
    });

    if (filteredRefiners.length > 0) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {filteredRefiners.map((submenu, index) => {
                        return <React.Fragment key={index}>{submenu}</React.Fragment>;
                    })}
                </Node>
            </Node>
        );
    }

    return null;
};

/**
 * Render Refiner Footer.
 * @param props - The Refine Menu view props.
 * @param handler - The Refine menu toggle function.
 * @param resources - Resources to get the done text and aria label.
 * @returns The JSX Element.
 */
const renderRefinerFooter = (
    props: IRefineMenuViewProps,
    handler: MouseEventHandler,
    resources: ICelliSearchResultContainerResources
): JSX.Element | null => {
    const { refiners } = props;
    if (refiners) {
        return (
            <Node className='ms-refine__footer__done'>
                <Node tag='button' className='ms-refine-filter-done' aria-label={resources.doneAriaLabel} tabIndex={0} onClick={handler}>
                    {resources.doneText}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render sort by drop down.
 * @param props - The sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @param isUserAuthenticated - If the user is authenticated.
 * @returns The JSX Element.
 */
const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string, isUserAuthenticated?: boolean): JSX.Element | null => {
    const { SortingContainer, sortByDropDown } = props;

    // @ts-ignore
    let filteredDropdownOptions = sortByDropDown.props.dropdownOptions;

    if (isUserAuthenticated === false) {
        // @ts-ignore
        filteredDropdownOptions = filteredDropdownOptions.filter(option => option.key !== 'Price-Asc' && option.key !== 'Price-Desc');
    }

    // @ts-ignore
    const updatedSortByDropDown = React.cloneElement(sortByDropDown, {
        // @ts-ignore
        ...sortByDropDown.props,
        dropdownOptions: filteredDropdownOptions
    });

    if (updatedSortByDropDown && !isRecoSearchPage) {
        return <Node {...SortingContainer}>{updatedSortByDropDown}</Node>;
    }

    return null;
};

/**
 * Render Title.
 * @param props - The title view props.
 * @returns The JSX Element.
 */
const renderTitle = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return (
            <Node {...TitleContainer}>
                <Node tag='h1' className=''>
                    {title.titlePrefix}
                    {title.titleText}
                </Node>
            </Node>
        );
    }
    return null;
};

export default SearchResultContainerView;
